import { and, eq, ilike, or } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useCallback, useMemo } from 'react'
import { FlatList, StyleSheet } from 'react-native'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useExportJobsXLSX } from '@/hooks/useExportJobsXLSX'
import { captureException } from '@/lib/errors'
import type { JobsExportRow } from '@/lib/export-jobs-xlsx'
import { performMutations } from '@/lib/mutations'
import { notify } from '@/lib/notice'
import { useStore } from '@/lib/pocketbase'
import { useJobsSearchQuery, useJobsStatusFilter } from '@/stores/jobs-filter-store'
import type { JobStatus } from '@/types/jobs'
import type { Addresses as Address, Customers as Customer } from '@/types/pbSchema'
import type { Tag } from '@/types/tags'
import { useCollectionRefresh } from '@/ui/CollectionRefreshControl'
import { JobCard } from '@/ui/jobs/JobCard'
import { JobsEmptyState } from '@/ui/jobs/JobsEmptyState'
import { JobsListHeader } from '@/ui/jobs/JobsListHeader'
import { ListWrapper } from '@/ui/ListWrapper'
import { LoadingIndicator } from '@/ui/LoadingIndicator'

export default function JobsPage() {
    const params = useLocalSearchParams<{ orgSlug: string }>()
    const router = useRouter()
    const { org, isLoading: isAuthLoading } = useCurrentOrg()
    const [jobsCollection, tagsCollection, customersCollection, addressesCollection] = useStore(
        'jobs',
        'tags',
        'customers',
        'addresses'
    )

    const searchQuery = useJobsSearchQuery()
    const statusFilter = useJobsStatusFilter()
    const { refreshControl } = useCollectionRefresh(['jobs'])

    const { isLoading: isAllJobsLoading, data: allJobs = [] } = useLiveQuery(q =>
        q.from({ jobs: jobsCollection }).where(({ jobs }) => eq(jobs.org, org?.id))
    )

    const { data: orgTags = [] } = useLiveQuery(
        q =>
            org?.id
                ? q.from({ tags: tagsCollection }).where(({ tags }) => eq(tags.org, org.id))
                : undefined,
        [org?.id]
    )

    const { data: orgCustomers = [] } = useLiveQuery(
        q =>
            org?.id
                ? q
                      .from({ customers: customersCollection })
                      .where(({ customers }) => eq(customers.org, org.id))
                : undefined,
        [org?.id]
    )

    const { data: orgAddresses = [] } = useLiveQuery(
        q =>
            org?.id
                ? q
                      .from({ addresses: addressesCollection })
                      .where(({ addresses }) => eq(addresses.org, org.id))
                : undefined,
        [org?.id]
    )

    const tagsById = useMemo(() => {
        const map = new Map<string, Tag>()
        for (const tag of orgTags as Tag[]) {
            map.set(tag.id, tag)
        }
        return map
    }, [orgTags])

    const customersById = useMemo(() => {
        const map = new Map<string, Customer>()
        for (const c of orgCustomers as Customer[]) {
            map.set(c.id, c)
        }
        return map
    }, [orgCustomers])

    const addressesById = useMemo(() => {
        const map = new Map<string, Address>()
        for (const a of orgAddresses as Address[]) {
            map.set(a.id, a)
        }
        return map
    }, [orgAddresses])

    const getJobTags = useCallback(
        (jobTagIds: string[] | undefined): Tag[] => {
            if (!jobTagIds?.length) return []
            return jobTagIds.map(id => tagsById.get(id)).filter((t): t is Tag => Boolean(t))
        },
        [tagsById]
    )

    const { data: rawJobs = [] } = useLiveQuery(
        q =>
            q
                .from({ jobs: jobsCollection })
                .where(({ jobs }) => {
                    const conditions = [eq(jobs.org, org?.id)]

                    if (statusFilter !== null) {
                        conditions.push(eq(jobs.status, statusFilter))
                    }

                    if (searchQuery) {
                        const pattern = `%${searchQuery}%`
                        conditions.push(
                            or(ilike(jobs.name, pattern), ilike(jobs.identifier, pattern))
                        )
                    }

                    if (conditions.length === 1) {
                        return conditions[0]
                    }
                    return and(...(conditions as [(typeof conditions)[0], (typeof conditions)[0]]))
                })
                .orderBy(({ jobs }) => jobs.created, 'desc'),
        [org?.id, searchQuery, statusFilter]
    )

    // Hydrate `job.expand.address` from the addressesById map so JobCard can
    // render addresses without depending on PocketBase expand data being
    // present on the realtime SSE event (it isn't).
    const jobs = useMemo(
        () =>
            rawJobs.map(job => ({
                ...job,
                expand: {
                    ...(job as { expand?: object }).expand,
                    address: job.address ? addressesById.get(job.address) : undefined,
                },
            })),
        [rawJobs, addressesById]
    )
    const isInitialLoading = isAuthLoading || isAllJobsLoading
    const hasAnyJobs = allJobs.length > 0

    const exportRows = useMemo<JobsExportRow[]>(
        () =>
            jobs.map(job => ({
                job,
                customer: job.customer ? customersById.get(job.customer) : undefined,
                address: job.address ? addressesById.get(job.address) : undefined,
                tags: getJobTags(job.tags),
            })),
        [jobs, customersById, addressesById, getJobTags]
    )

    const { exportToXLSX, isExporting } = useExportJobsXLSX({
        rows: exportRows,
        filenamePrefix: `jobs-${params.orgSlug ?? 'export'}`,
    })

    const handleAddJob = useCallback(() => {
        router.push(`/${params.orgSlug}/settings/jobs/new`)
    }, [router, params.orgSlug])

    const updateJobStatus = useCallback(
        async (jobId: string, status: JobStatus) => {
            try {
                await performMutations(function* () {
                    yield jobsCollection.update(jobId, draft => {
                        draft.status = status
                    })
                })
            } catch (err) {
                captureException('Failed to update job status', err, { jobId, status })
                notify.error('Failed to update job status')
            }
        },
        [jobsCollection]
    )

    if (isInitialLoading) {
        return (
            <ListWrapper>
                <LoadingIndicator />
            </ListWrapper>
        )
    }

    return (
        <ListWrapper>
            <FlatList
                data={jobs}
                keyExtractor={job => job.id}
                renderItem={({ item }) => (
                    <JobCard
                        job={item}
                        tags={getJobTags(item.tags)}
                        onChangeStatus={updateJobStatus}
                    />
                )}
                ListHeaderComponent={
                    hasAnyJobs ? (
                        <JobsListHeader
                            onExport={exportToXLSX}
                            isExporting={isExporting}
                            canExport={jobs.length > 0}
                        />
                    ) : null
                }
                ListEmptyComponent={
                    <JobsEmptyState hasAnyJobs={hasAnyJobs} onAddJob={handleAddJob} />
                }
                contentContainerStyle={styles.container}
                showsVerticalScrollIndicator={false}
                refreshControl={refreshControl}
            />
        </ListWrapper>
    )
}

const styles = StyleSheet.create({
    container: {
        padding: 16,
        gap: 12,
    },
})
